// Public team-invitation accept page (#17), reachable without auth at
// /accept-invite?token=<64-hex>. Previews the invite, lets a logged-in user
// accept, and routes a logged-out invitee into signup with the invited email
// prefilled + locked (the server 403s at accept if the account email differs).
// Ported from v1 client/client/src/pages/AcceptInvite.tsx.

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, XCircle, Users, Loader2, Shield, User } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/state/useAuth'
import { useTeam } from '@/state/useTeam'
import { useRestaurantProfile } from '@/hooks/useRestaurantProfile'
import { storeInviteRedirect } from '@/lib/inviteRedirect'
import * as keycast from '@/lib/keycastClient'
import type { InvitationPreview } from '@/lib/keycastClient'
import { APP_LANDING_PATH } from '@/shell/navRegistry'

const TOKEN_RE = /^[0-9a-f]{64}$/i

type PageState =
  | { kind: 'loading' }
  | { kind: 'preview'; preview: InvitationPreview }
  | { kind: 'accepting'; preview: InvitationPreview }
  | { kind: 'accepted'; teamName: string }
  | { kind: 'error'; message: string; showSignOut?: boolean }

function formatExpiry(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function AcceptInvitePage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const invitePath = `/accept-invite?token=${encodeURIComponent(token)}`

  const isAuthenticated = useAuth((s) => s.isAuthenticated)
  const authStatus = useAuth((s) => s.status)
  const initialize = useAuth((s) => s.initialize)
  const logout = useAuth((s) => s.logout)
  const loadTeams = useTeam((s) => s.loadTeams)

  const [state, setState] = useState<PageState>({ kind: 'loading' })

  // This page is PUBLIC (outside AuthGate), so nothing else initializes auth.
  // Kick it off so the accept-vs-signup CTA reflects whether the visitor is
  // already logged in. The preview fetch below does NOT wait on this.
  useEffect(() => {
    if (authStatus === 'idle') void initialize()
  }, [authStatus, initialize])

  // Load the invitation preview. The preview endpoint is public — it does not
  // depend on auth, so fetch immediately rather than waiting for auth to settle
  // (waiting would hang forever on this route, since auth starts 'idle').
  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: t('inviteMissing', { defaultValue: 'This invitation link is missing a token.' }) })
      return
    }
    if (!TOKEN_RE.test(token)) {
      setState({ kind: 'error', message: t('inviteMalformed', { defaultValue: 'This invitation link is malformed.' }) })
      return
    }
    let cancelled = false
    keycast
      .previewInvitation(token)
      .then((preview) => {
        if (!cancelled) setState({ kind: 'preview', preview })
      })
      .catch((err) => {
        if (cancelled) return
        setState({ kind: 'error', message: mapPreviewError(err, t) })
      })
    return () => {
      cancelled = true
    }
  }, [token, t])

  async function handleAccept() {
    if (state.kind !== 'preview') return
    const { preview } = state
    setState({ kind: 'accepting', preview })
    try {
      await keycast.acceptInvitation(token)
      await loadTeams()
      // Scrub the bearer token from history.
      window.history.replaceState(null, '', '/accept-invite')
      setState({ kind: 'accepted', teamName: preview.team_name })
    } catch (err) {
      if (err instanceof keycast.KeycastError && err.status === 409) {
        // Already a member — treat as success.
        await loadTeams()
        window.history.replaceState(null, '', '/accept-invite')
        setState({ kind: 'accepted', teamName: preview.team_name })
        return
      }
      if (err instanceof keycast.KeycastError && err.status === 403) {
        setState({
          kind: 'error',
          message: t('inviteWrongEmail', {
            defaultValue: `This invitation was sent to ${preview.email}. Sign out and sign in with that address to accept it.`,
            email: preview.email,
          }),
          showSignOut: true,
        })
        return
      }
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : t('inviteAcceptFailed', { defaultValue: 'Failed to accept invitation.' }),
      })
    }
  }

  async function handleSignOut() {
    await logout()
    navigate(`/sign-in?redirect=${encodeURIComponent(invitePath)}`)
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm">
        {state.kind === 'loading' && (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t('inviteLoading', { defaultValue: 'Loading invitation…' })}</p>
          </div>
        )}

        {(state.kind === 'preview' || state.kind === 'accepting') && (
          <>
            <InviteCard preview={state.preview} t={t} />
            {authStatus === 'idle' || authStatus === 'loading' ? (
              // Auth still settling — don't flash the wrong CTA.
              <div className="mt-4 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : isAuthenticated ? (
              <Button className="mt-4 w-full" onClick={handleAccept} disabled={state.kind === 'accepting'}>
                {state.kind === 'accepting'
                  ? t('inviteJoining', { defaultValue: 'Joining…' })
                  : t('inviteAccept', { defaultValue: 'Accept Invitation' })}
              </Button>
            ) : (
              <div className="mt-4 space-y-2">
                <p className="text-center text-sm text-muted-foreground">
                  {t('inviteCreateAccount', { defaultValue: 'Create an account to accept this invitation.' })}
                </p>
                <Link
                  to={`/sign-in?mode=signup&redirect=${encodeURIComponent(invitePath)}&inviteEmail=${encodeURIComponent(state.preview.email)}`}
                  onClick={() => storeInviteRedirect(invitePath)}
                  className="block"
                >
                  <Button className="w-full">{t('inviteCreateAccountButton', { defaultValue: 'Create account' })}</Button>
                </Link>
                <Link
                  to={`/sign-in?redirect=${encodeURIComponent(invitePath)}&inviteEmail=${encodeURIComponent(state.preview.email)}`}
                  onClick={() => storeInviteRedirect(invitePath)}
                  className="block text-center text-sm text-primary hover:underline"
                >
                  {t('inviteHaveAccount', { defaultValue: 'Already have an account? Sign in' })}
                </Link>
              </div>
            )}
          </>
        )}

        {state.kind === 'accepted' && (
          <div className="flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            <p className="text-lg font-semibold text-foreground">{t('inviteAccepted', { defaultValue: "You're in!" })}</p>
            <p className="text-sm text-muted-foreground">
              {t('inviteJoinedTeam', { defaultValue: `You've joined ${state.teamName}.`, teamName: state.teamName })}
            </p>
            <Button className="mt-2 w-full" onClick={() => navigate(APP_LANDING_PATH)}>
              {t('inviteContinue', { defaultValue: 'Continue' })}
            </Button>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="flex flex-col items-center gap-3 text-center">
            <XCircle className="h-10 w-10 text-destructive" />
            <p className="text-lg font-semibold text-foreground">{t('inviteUnavailable', { defaultValue: 'Invitation Unavailable' })}</p>
            <p className="max-w-xs text-sm text-muted-foreground">{state.message}</p>
            {state.showSignOut && (
              <Button variant="secondary" className="mt-2" onClick={handleSignOut}>
                {t('inviteSignOut', { defaultValue: 'Sign out and use a different account' })}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function mapPreviewError(err: unknown, t: ReturnType<typeof useTranslation>['t']): string {
  if (err instanceof keycast.KeycastError && err.status === 404) {
    return t('inviteInvalid', {
      defaultValue: 'This invitation is invalid, has expired, or has already been used.',
    })
  }
  return err instanceof Error ? err.message : t('inviteLoadFailed', { defaultValue: 'Could not load this invitation.' })
}

function InviteCard({
  preview,
  t,
}: {
  preview: InvitationPreview
  t: ReturnType<typeof useTranslation>['t']
}) {
  const { picture, name } = useRestaurantProfile(preview.team_key_pubkey)
  const restaurantName = (name?.trim() || preview.team_name) ?? ''
  const initial = restaurantName.charAt(0).toUpperCase() || '?'
  const roleLabel = preview.role.toLowerCase() === 'admin'
    ? t('inviteRoleAdmin', { defaultValue: 'Admin' })
    : t('inviteRoleMember', { defaultValue: 'Member' })

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex flex-col items-center">
        {picture ? (
          <img
            src={picture}
            alt=""
            className="h-16 w-16 rounded-full border border-border object-cover"
            onError={(e) => {
              const img = e.currentTarget
              img.style.display = 'none'
              const fb = img.nextElementSibling as HTMLElement | null
              if (fb) fb.style.display = 'flex'
            }}
          />
        ) : null}
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full border border-border bg-muted text-2xl font-semibold text-muted-foreground"
          style={{ display: picture ? 'none' : 'flex' }}
        >
          {preview.team_key_pubkey ? initial : <Users className="h-8 w-8" />}
        </div>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {t('inviteHeadline', { defaultValue: "You've been invited to join" })}
        </p>
        <h1 className="text-center text-lg font-bold text-foreground">{restaurantName}</h1>
      </div>

      <div className="mt-4 space-y-2 text-sm">
        <Field label={t('inviteFrom', { defaultValue: 'From' })} value={preview.invited_by_display_name || preview.invited_by_email} />
        <Field label={t('inviteTo', { defaultValue: 'To' })} value={preview.email} />
        <Field
          label={t('inviteRole', { defaultValue: 'Role' })}
          value={
            <span className="inline-flex items-center gap-1">
              {preview.role.toLowerCase() === 'admin' ? <Shield className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
              {roleLabel}
            </span>
          }
        />
        <Field label={t('inviteExpires', { defaultValue: 'Expires' })} value={formatExpiry(preview.expires_at)} />
      </div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate text-right font-medium text-foreground">{value}</span>
    </div>
  )
}
